import React, { useEffect, useState } from 'react'
import TextField from '@material-ui/core/TextField'
import CircularProgress from '@material-ui/core/CircularProgress'
import Autocomplete from '@material-ui/lab/Autocomplete'

import PartnersApi from '../../../../api/PartnersApi'

/**
 * Only the four fields the picker needs. GET /api/partners serves a partner
 * with `default_includes = '**'`, so each row also carries tracks, sso_infos,
 * domains and five uploader-backed image fields -- none of which this component
 * reads, and all of which are the reason PAGE_SIZE below is not optional.
 */
export interface PartnerOption {
  id: number
  name: string
  slug: string
  location?: string | null
}

/**
 * PartnerService#list does `limit(@page_size)` with no default, so omitting
 * page_size does not mean "a sensible page" -- it means all 170 partners, fully
 * inlined, on every keystroke.
 */
const PAGE_SIZE = 25

/** Long enough that typing a partner name is one request, not one per letter. */
const SEARCH_DEBOUNCE_MS = 300

interface Props {
  value: PartnerOption[]
  onChange(partners: PartnerOption[]): void
  label?: string
  id?: string
}

/**
 * A searchable, multi-select partner picker backed by the public partner index.
 *
 * Deliberately not AutocompleteMui: that one is freeSolo, single-select, and its
 * option type has no id -- and an id is the entire point here, because the embed
 * scopes by partner id, not by slug. Deliberately not AdminDashboard's
 * PartnerSelector either: its list is scoped to `user.partnerSlugs`, so a super
 * admin building an embed for a customer would not find that customer in it.
 */
const PartnerMultiSelect: React.FC<Props> = ({ value, onChange, label = 'Partners', id }) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<PartnerOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return undefined

    let cancelled = false
    setLoading(true)

    const timer = setTimeout(() => {
      PartnersApi.getAll({ page_size: PAGE_SIZE, search: inputValue || undefined })
        .then((res: any) => {
          if (cancelled) return
          setOptions(Array.isArray(res?.json) ? res.json : [])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)

    // Cancels both the pending timer and the in-flight response. Without the
    // flag, a slow first request can resolve after a later, narrower one and
    // overwrite the newer options with stale results.
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [inputValue, open])

  const selectedIds = value.map((partner) => partner.id)

  return (
    // The id goes on a wrapper, not on the rendered input. MUI derives the
    // listbox and aria-labelledby ids from Autocomplete's own `id`, and
    // overriding that through renderInput breaks the popup's a11y wiring.
    // Tests target `#<id> input`.
    <div id={id}>
      <Autocomplete
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        value={value}
        loading={loading}
        // The search is server-side and spans name, location and slug. MUI's
        // default filter would then re-filter those results against the input on
        // the client and drop every row that matched on a field it cannot see.
        filterOptions={(opts) => opts}
        getOptionSelected={(option, selected) => option.id === selected.id}
        getOptionLabel={(option) => option.name}
        onChange={(_event, selected) => onChange(selected as PartnerOption[])}
        onInputChange={(_event, next, reason) => {
          if (reason === 'input') setInputValue(next)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            size='small'
            label={label}
            placeholder='Search partners'
            // The generated URL carries ids, but a human picks names. Showing the
            // resolved ids makes the snippet checkable without decoding the URL.
            helperText={selectedIds.length > 0 ? `Partner IDs: ${selectedIds.join(', ')}` : 'All partners'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </div>
  )
}

export default PartnerMultiSelect
